import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Plus, Trash, PencilSimple, MagnifyingGlass, Check, UsersFour} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { TERRITOIRES } from '../constants/countries';
import { PageHero } from '../components/motion/PageHero';
import {
  listEmailSegments,
  createEmailSegment,
  updateEmailSegment,
  deleteEmailSegment,
  resolveEmailSegment,
  previewEmailSegment,
} from '../services/api';

const ENTITY_LABELS = { contacts: 'Contacts', organizations: 'Organisations', cmc_cooperators: 'Coopérateurs' };
const STATUT_OPTIONS = ['prospect', 'adherent', 'ancien_adherent', 'actif', 'inactif', 'archive'];
const ORG_TYPE_OPTIONS = ['SCOP', 'SCIC', 'association', 'entreprise', 'cooperative', 'autre'];
const TYPES_DOSSIER = [
  { value: 'mission_coop_cipaesse', label: 'Mission coopérative CIPAESSE' },
  { value: 'mission_coop_cipascop', label: 'Mission coopérative CIPASCOP' },
  { value: 'adhesion_ftpe', label: 'Adhésion FTPE' },
  { value: 'adhesion_formation', label: 'Adhésion formation' },
  { value: 'salle_formation', label: 'Salle formation' },
  { value: 'convention_cadre_formascop', label: 'Convention-cadre FORMASCOP' },
  { value: 'accompagnement_cooperatif', label: 'Accompagnement coopératif' },
  { value: 'copsecur', label: 'COPSECUR' },
  { value: 'rcds', label: 'RCDS' },
  { value: 'domium', label: 'DOMIUM' },
  { value: 'formation', label: 'Formation' },
  { value: 'financement', label: 'Financement' },
];

export default function EmailSegmentsPage() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const emptyForm = {
    name: '',
    description: '',
    filters: {
      territoires: [],
      statuts_adhesion: [],
      org_types: [],
      types_dossier: [],
      tags: [],
      entities: ['contacts', 'organizations', 'cmc_cooperators'],
      match: 'AND',
    },
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listEmailSegments();
      setSegments(r.data.items || []);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm(emptyForm); setPreview(null); };
  const startEdit = (seg) => {
    setEditing(seg);
    setForm({
      name: seg.name || '',
      description: seg.description || '',
      filters: {
        territoires: seg.filters?.territoires || [],
        statuts_adhesion: seg.filters?.statuts_adhesion || [],
        org_types: seg.filters?.org_types || [],
        types_dossier: seg.filters?.types_dossier || [],
        tags: seg.filters?.tags || [],
        entities: seg.filters?.entities || ['contacts', 'organizations', 'cmc_cooperators'],
        match: seg.filters?.match || 'AND',
      },
    });
  };

  const toggleArr = (key, val) => {
    const cur = form.filters[key] || [];
    const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
    setForm({ ...form, filters: { ...form.filters, [key]: next } });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.warning('Nom requis');
    try {
      if (editing) {
        await updateEmailSegment(editing.id, form);
        toast.success('Segment mis à jour');
      } else {
        await createEmailSegment(form);
        toast.success('Segment créé');
      }
      startNew();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const remove = async (seg) => {
    if (!window.confirm(`Supprimer "${seg.name}" ?`)) return;
    await deleteEmailSegment(seg.id);
    toast.success('Segment supprimé');
    load();
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await previewEmailSegment(form.filters);
      setPreview(r.data);
    } catch (e) {
      toast.error('Erreur preview');
    }
    setPreviewing(false);
  };

  const loadResolved = async (seg) => {
    try {
      const r = await resolveEmailSegment(seg.id);
      setPreview({ ...r.data });
      startEdit(seg);
    } catch {}
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="email-segments-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Segments ciblés"
        subtitle="Regroupement de destinataires pour vos campagnes"
        icon={<UsersFour size={24} weight="duotone" className="text-white" />}
        gradient="from-[#D5007D] to-[#EA3C3A]"
        accents={['#00AEEF', '#80C51F']}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2421] flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <Users size={28} className="text-[#2D5A4C]" weight="fill" />
            Segments ciblés
          </h1>
          <p className="text-[#5E6D66] mt-1">Groupes de destinataires réutilisables pour les campagnes email</p>
        </div>
        <Button onClick={startNew} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="new-segment-btn">
          <Plus size={16} className="mr-2" /> Nouveau segment
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-[#E8E6E1] lg:col-span-1">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-2">Segments enregistrés ({segments.length})</h3>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            <div className="space-y-2 max-h-[560px] overflow-y-auto" data-testid="segments-list">
              {segments.map((s) => (
                <div key={s.id} className="p-3 rounded-md border border-[#E8E6E1] bg-white hover:bg-[#F9F8F5]">
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={() => loadResolved(s)} className="text-left flex-1" data-testid={`segment-${s.id}`}>
                      <p className="text-sm font-medium">{s.name}</p>
                      {s.description && <p className="text-[10px] text-[#5E6D66] line-clamp-1">{s.description}</p>}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(s.filters?.territoires || []).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                        {(s.filters?.statuts_adhesion || []).map((t) => <Badge key={t} variant="outline" className="text-[10px] border-[#2D5A4C]/40">{t}</Badge>)}
                        {(s.filters?.types_dossier || []).map((t) => <Badge key={t} className="bg-purple-100 text-purple-800 text-[10px]">{t}</Badge>)}
                      </div>
                    </button>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" onClick={() => startEdit(s)}><PencilSimple size={14} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(s)} className="text-red-600"><Trash size={14} /></Button>
                    </div>
                  </div>
                </div>
              ))}
              {segments.length === 0 && !loading && <p className="text-xs text-[#5E6D66]">Aucun segment</p>}
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E8E6E1] lg:col-span-2">
          <CardContent className="p-4 space-y-4">
            <h3 className="text-sm font-semibold">{editing ? `Modifier "${editing.name}"` : 'Nouveau segment'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nom</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 border-[#E8E6E1]" data-testid="segment-name" />
              </div>
              <div>
                <Label className="text-xs">Match</Label>
                <Select value={form.filters.match} onValueChange={(v) => setForm({ ...form, filters: { ...form.filters, match: v } })}>
                  <SelectTrigger className="mt-1 border-[#E8E6E1]" data-testid="segment-match"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">ET (tous les critères)</SelectItem>
                    <SelectItem value="OR">OU (au moins un)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 border-[#E8E6E1]" />
            </div>

            <div>
              <Label className="text-xs">Entités à inclure</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {Object.entries(ENTITY_LABELS).map(([key, label]) => {
                  const active = form.filters.entities.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleArr('entities', key)}
                      className={`px-3 py-1 rounded-full text-xs border ${active ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1]'}`}
                      data-testid={`segment-entity-${key}`}
                    >
                      {active && <Check size={12} className="inline mr-1" />}{label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs">Territoires</Label>
              <div className="flex gap-1 mt-1 flex-wrap max-h-32 overflow-y-auto p-2 border border-[#E8E6E1] rounded-md">
                {TERRITOIRES.map((t) => {
                  const active = form.filters.territoires.includes(t.value);
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => toggleArr('territoires', t.value)}
                      className={`px-2 py-0.5 rounded-full text-[10px] border ${active ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1]'}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Statuts d'adhésion</Label>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {STATUT_OPTIONS.map((s) => {
                    const active = form.filters.statuts_adhesion.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleArr('statuts_adhesion', s)}
                        className={`px-2 py-0.5 rounded-full text-[10px] border ${active ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1]'}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs">Types d'organisation</Label>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {ORG_TYPE_OPTIONS.map((s) => {
                    const active = form.filters.org_types.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleArr('org_types', s)}
                        className={`px-2 py-0.5 rounded-full text-[10px] border ${active ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1]'}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Types de dossier (missions coopératives, adhésion formation, etc.)</Label>
              <div className="flex gap-1 mt-1 flex-wrap max-h-24 overflow-y-auto p-2 border border-[#E8E6E1] rounded-md">
                {TYPES_DOSSIER.map((t) => {
                  const active = form.filters.types_dossier.includes(t.value);
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => toggleArr('types_dossier', t.value)}
                      className={`px-2 py-0.5 rounded-full text-[10px] border ${active ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-[#E8E6E1]'}`}
                      data-testid={`segment-type-dossier-${t.value}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#5E6D66] mt-1">Filtre les contacts et organisations liés à des dossiers de ces types.</p>
            </div>

            <div className="flex gap-2 pt-2 border-t border-[#E8E6E1]">
              <Button variant="outline" onClick={runPreview} disabled={previewing} className="border-[#E8E6E1]" data-testid="preview-segment-btn">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MagnifyingGlass size={14} className="mr-2" />}
                Aperçu
              </Button>
              <Button onClick={save} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-segment-btn">
                {editing ? 'Mettre à jour' : 'Enregistrer'}
              </Button>
            </div>

            {preview && (
              <div className="p-3 bg-[#F9F8F5] rounded-md border border-[#E8E6E1] space-y-2" data-testid="segment-preview">
                <p className="text-xs font-semibold">Résultat : {preview.total} destinataire(s)</p>
                <div className="flex gap-2 flex-wrap text-[10px]">
                  <Badge variant="outline">Contacts : {preview.counts?.contacts || 0}</Badge>
                  <Badge variant="outline">Organisations : {preview.counts?.organizations || 0}</Badge>
                  <Badge variant="outline">Coopérateurs : {preview.counts?.cmc_cooperators || 0}</Badge>
                </div>
                <div className="max-h-40 overflow-y-auto text-[10px] text-[#5E6D66] font-mono">
                  {(preview.emails || []).slice(0, 20).map((e) => <div key={e}>{e}</div>)}
                  {(preview.emails || []).length > 20 && <div>… et {preview.emails.length - 20} de plus</div>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
